import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import axios from 'axios';
import {
  confirmAdminPasswordReset,
  fetchAdminSession,
  loginAdmin,
  logoutAdmin,
  requestAdminPasswordReset,
} from '../services/adminApi';
import { getApiErrorMessage } from '../services/apiUtils';
import type { AdminAuthSession, AdminUserRecord } from '../types/admin';

type SignInPayload = {
  email: string;
  password: string;
};

type AdminAccessStatus = 'idle' | 'checking' | 'authorized' | 'denied' | 'fallback';
type AdminAuthEvent = 'INITIAL_SESSION' | 'SIGNED_IN' | 'SIGNED_OUT' | 'SESSION_REFRESHED';

type PasswordResetRequestResult = {
  error: string | null;
  setupUrl?: string;
};

type AuthContextValue = {
  isConfigured: boolean;
  isLoading: boolean;
  isCheckingAccess: boolean;
  session: AdminAuthSession | null;
  user: AdminUserRecord | null;
  adminProfile: AdminUserRecord | null;
  adminAccessStatus: AdminAccessStatus;
  adminAccessError: string | null;
  lastAuthEvent: AdminAuthEvent | null;
  signInWithPassword: (payload: SignInPayload) => Promise<string | null>;
  signOut: () => Promise<string | null>;
  requestPasswordReset: (email: string) => Promise<PasswordResetRequestResult>;
  updatePassword: (password: string, token: string) => Promise<string | null>;
  refreshAdminAccess: () => Promise<AdminUserRecord | null>;
};

const TAB_REVALIDATION_THRESHOLD_MS = 5 * 60 * 1000;
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminAuthSession | null>(null);
  const [lastAuthEvent, setLastAuthEvent] = useState<AdminAuthEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [adminProfile, setAdminProfile] = useState<AdminUserRecord | null>(null);
  const [adminAccessStatus, setAdminAccessStatus] = useState<AdminAccessStatus>('idle');
  const [adminAccessError, setAdminAccessError] = useState<string | null>(null);
  const hiddenAtRef = useRef<number | null>(null);

  const resetAdminState = useCallback(() => {
    setSession(null);
    setAdminProfile(null);
    setAdminAccessStatus('idle');
    setAdminAccessError(null);
  }, []);

  const loadSession = useCallback(async (silent = false) => {
    if (!silent) {
      setAdminAccessStatus('checking');
      setAdminAccessError(null);
    }

    try {
      const nextSession = await fetchAdminSession();
      setSession(nextSession);
      setAdminProfile(nextSession.user);
      setAdminAccessStatus('authorized');
      setAdminAccessError(null);
      setLastAuthEvent((current) => current || 'INITIAL_SESSION');
      return nextSession.user;
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const message = getApiErrorMessage(error, 'Não foi possível confirmar o acesso ao painel.');
      if (status === 401) {
        resetAdminState();
        return null;
      }

      setSession(null);
      setAdminProfile(null);
      setAdminAccessStatus(status === 403 ? 'denied' : 'fallback');
      setAdminAccessError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [resetAdminState]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (!session || !hiddenAt || Date.now() - hiddenAt < TAB_REVALIDATION_THRESHOLD_MS) return;
      setLastAuthEvent('SESSION_REFRESHED');
      void loadSession(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadSession, session]);

  const signInWithPassword = useCallback(async ({ email, password }: SignInPayload) => {
    try {
      const nextSession = await loginAdmin(email.trim(), password);
      setSession(nextSession);
      setAdminProfile(nextSession.user);
      setAdminAccessStatus('authorized');
      setAdminAccessError(null);
      setLastAuthEvent('SIGNED_IN');
      return null;
    } catch (error) {
      resetAdminState();
      return getApiErrorMessage(error, 'Não foi possível realizar o login.');
    }
  }, [resetAdminState]);

  const signOut = useCallback(async () => {
    try {
      await logoutAdmin();
      return null;
    } catch (error) {
      return getApiErrorMessage(error, 'Não foi possível encerrar a sessão no servidor.');
    } finally {
      resetAdminState();
      setLastAuthEvent('SIGNED_OUT');
    }
  }, [resetAdminState]);

  const requestPasswordReset = useCallback(async (email: string): Promise<PasswordResetRequestResult> => {
    try {
      const response = await requestAdminPasswordReset(email.trim());
      return { error: null, setupUrl: response.setupUrl };
    } catch (error) {
      return {
        error: getApiErrorMessage(error, 'Não foi possível solicitar a redefinição de senha.'),
      };
    }
  }, []);

  const updatePassword = useCallback(async (password: string, token: string) => {
    try {
      await confirmAdminPasswordReset(token, password);
      return null;
    } catch (error) {
      return getApiErrorMessage(error, 'Não foi possível definir a nova senha.');
    }
  }, []);

  const refreshAdminAccess = useCallback(async () => loadSession(), [loadSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isConfigured: true,
      isLoading,
      isCheckingAccess: adminAccessStatus === 'checking',
      session,
      user: session?.user ?? null,
      adminProfile,
      adminAccessStatus,
      adminAccessError,
      lastAuthEvent,
      signInWithPassword,
      signOut,
      requestPasswordReset,
      updatePassword,
      refreshAdminAccess,
    }),
    [
      adminAccessError,
      adminAccessStatus,
      adminProfile,
      isLoading,
      lastAuthEvent,
      refreshAdminAccess,
      requestPasswordReset,
      session,
      signInWithPassword,
      signOut,
      updatePassword,
    ]
  );

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider.');
  return context;
}
